import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  MessageCircle,
  Send,
  ArrowLeft,
  User,
  Crown,
  Loader2,
  Search
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = '/api';

const MessagesPage = () => {
  const [conversations, setConversations] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [view, setView] = useState('list'); // list, chat, instructors
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const currentUserId = JSON.parse(localStorage.getItem('user') || '{}')?.id;

  useEffect(() => {
    fetchData();
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
      // Start polling
      pollIntervalRef.current = setInterval(() => {
        fetchMessages(selectedConversation.id);
      }, 5000);
    }
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [selectedConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const [convRes, instRes, meRes] = await Promise.all([
        axios.get(`${API}/messages/conversations`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/instructors/messageable`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setConversations(convRes.data || []);
      setInstructors(instRes.data || []);
      setIsPremium(meRes.data?.is_premium || false);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(
        `${API}/messages/conversation/${conversationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async (receiverId) => {
    if (!newMessage.trim()) return;

    const token = localStorage.getItem('token');
    setSending(true);

    try {
      const response = await axios.post(
        `${API}/messages/send/${receiverId}`,
        { content: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages([...messages, response.data]);
      setNewMessage('');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Mesaj göndermek için Premium üyelik gereklidir');
      } else {
        toast.error('Mesaj gönderilemedi');
      }
    } finally {
      setSending(false);
    }
  };

  const startConversation = async (instructor) => {
    const token = localStorage.getItem('token');
    if (!isPremium) {
      toast.error('Mesaj göndermek için Premium üyelik gereklidir');
      return;
    }

    // Check if conversation exists
    const existingConv = conversations.find(c =>
      c.participants?.includes(instructor.user_id)
    );

    if (existingConv) {
      setSelectedConversation(existingConv);
      setView('chat');
    } else {
      // Create new conversation by sending first message
      setSelectedConversation({
        id: null,
        other_user: {
          id: instructor.user_id,
          name: instructor.user?.name || instructor.full_name,
          avatar: instructor.user?.avatar
        },
        is_instructor: true
      });
      setMessages([]);
      setView('chat');
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // Chat View
  if (view === 'chat' && selectedConversation) {
    const otherUser = selectedConversation.other_user;
    const receiverId = otherUser?.id;

    return (
      <div className="min-h-screen pt-16 bg-gray-50 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b px-4 py-3 flex items-center gap-3">
          <button onClick={() => { setView('list'); setSelectedConversation(null); }}>
            <ArrowLeft className="h-5 w-5" />
          </button>
          <Avatar>
            <AvatarImage src={otherUser?.avatar} />
            <AvatarFallback>{otherUser?.name?.[0]}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-semibold">{otherUser?.name}</p>
            {selectedConversation.is_instructor && (
              <Badge variant="outline" className="text-xs">Eğitmen</Badge>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              Henüz mesaj yok. İlk mesajı sen gönder! 💬
            </div>
          )}
          {messages.map(msg => (
            <div
              key={msg.id}
              className={`flex ${msg.sender_id === currentUserId ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[70%] px-4 py-2 rounded-2xl ${
                  msg.sender_id === currentUserId
                    ? 'bg-purple-600 text-white rounded-br-md'
                    : 'bg-white text-gray-800 rounded-bl-md shadow'
                }`}
              >
                <p>{msg.content}</p>
                <p className={`text-xs mt-1 ${msg.sender_id === currentUserId ? 'text-purple-200' : 'text-gray-400'}`}>
                  {formatTime(msg.created_at)}
                </p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        {isPremium ? (
          <div className="bg-white border-t p-4">
            <div className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Mesajınız..."
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(receiverId)}
              />
              <Button onClick={() => handleSendMessage(receiverId)} disabled={sending || !newMessage.trim()}>
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-purple-50 border-t p-4 text-center">
            <p className="text-purple-800 mb-2">Mesaj göndermek için Premium üyelik gerekli</p>
            <Link to="/pricing">
              <Button className="bg-purple-600 hover:bg-purple-700">
                <Crown className="h-4 w-4 mr-2" />
                Premium'a Yükselt
              </Button>
            </Link>
          </div>
        )}
      </div>
    );
  }

  // List View
  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-purple-50 to-white">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6">Mesajlar 💬</h1>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={view === 'list' ? 'default' : 'outline'}
            onClick={() => setView('list')}
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            Sohbetlerim
          </Button>
          <Button
            variant={view === 'instructors' ? 'default' : 'outline'}
            onClick={() => setView('instructors')}
          >
            <User className="h-4 w-4 mr-2" />
            Eğitmenler
          </Button>
        </div>

        {/* Conversations List */}
        {view === 'list' && (
          <div className="space-y-3">
            {conversations.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <MessageCircle className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Henüz mesaj yok</p>
                  <p className="text-sm text-gray-400 mt-2">Bir eğitmenle konuşmaya başlayın</p>
                </CardContent>
              </Card>
            ) : (
              conversations.map(conv => (
                <Card
                  key={conv.id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => { setSelectedConversation(conv); setView('chat'); }}
                >
                  <CardContent className="p-4 flex items-center gap-3">
                    <Avatar>
                      <AvatarImage src={conv.other_user?.avatar} />
                      <AvatarFallback>{conv.other_user?.name?.[0]}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">{conv.other_user?.name}</p>
                        {conv.is_instructor && <Badge variant="outline" className="text-xs">Eğitmen</Badge>}
                      </div>
                      <p className="text-sm text-gray-500 truncate">{conv.last_message_preview}</p>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Instructors List */}
        {view === 'instructors' && (
          <div className="space-y-3">
            {instructors.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <p className="text-gray-500">Henüz mesaj gönderebileceğiniz eğitmen yok</p>
                </CardContent>
              </Card>
            ) : (
              instructors.map(inst => (
                <Card
                  key={inst.id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => startConversation(inst)}
                >
                  <CardContent className="p-4 flex items-center gap-3">
                    <Avatar>
                      <AvatarImage src={inst.user?.avatar} />
                      <AvatarFallback>{(inst.user?.name || inst.full_name)?.[0]}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-semibold">{inst.user?.name || inst.full_name}</p>
                      <p className="text-sm text-gray-500">{inst.expertise_area}</p>
                    </div>
                    <Button size="sm" variant="outline">
                      <MessageCircle className="h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))
            )}

            {!isPremium && (
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-4 text-center">
                  <Crown className="h-8 w-8 mx-auto text-purple-600 mb-2" />
                  <p className="text-purple-800 font-medium">Premium üyeler eğitmenlerle mesajlaşabilir</p>
                  <Link to="/pricing">
                    <Button className="mt-3 bg-purple-600 hover:bg-purple-700">
                      Premium'a Yükselt
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;
